// Single source of truth for the `generate-crew-schedule` edge-function
// request body.
//
// Two call sites invoke the function: the AI settings dialog
// (`AiGenerationDialog.tsx`, first generation) and the "regenerate with
// instructions" flow (`CrewSchedule.tsx`). They previously each built
// the body by hand and had drifted apart — the regenerate path omitted
// `briefingStages` (so the server could not resolve any stage), dropped
// crew stage preferences, and shaped `existingAssignments` with the
// wrong field names. Centralising the body here keeps every setting from
// the "Generate with AI" window flowing through on BOTH paths.

export interface ScheduleCrewInput {
  id: string;
  name: string;
  function?: string;
  /** Producer's preferred stage labels for this crew member. */
  preferredStages?: string[];
  /** True when the crew member should only get custom subjects + breaks. */
  noStage?: boolean;
}

export interface ScheduleArtistInput {
  name: string;
  stage: string;
  start: string;
  end: string;
}

export interface ScheduleStageInput {
  id: string;
  label: string;
}

export interface ScheduleCustomSubjectInput {
  subject: string;
  location?: string;
  stage_id?: string | null;
  frequency: number;
  duration: number;
  preferredTimeStart?: string;
  preferredTimeEnd?: string;
  preferredCrewMember?: string;
}

export interface ScheduleCrewBreakInput {
  time: string;
  duration: number;
}

/** An already-placed assignment the AI must schedule around. */
export interface ScheduleExistingAssignmentInput {
  crew_member_id?: string;
  artist_name?: string;
  subject?: string;
  stage_name?: string;
  filming_start?: string;
  filming_end?: string;
  assignment_type?: string;
}

export interface BuildScheduleRequestParams {
  briefingId: string;
  date: string;
  crew: ScheduleCrewInput[];
  artists: ScheduleArtistInput[];
  briefingStages: ScheduleStageInput[];
  customSubjects: ScheduleCustomSubjectInput[];
  crewBreaks: ScheduleCrewBreakInput[];
  existingAssignments: ScheduleExistingAssignmentInput[];
  defaultDuration: number;
  travelTime: number;
  slotMinutes?: number;
  regenerateInstructions?: string;
}

/**
 * Assemble the edge-function request body. The shape mirrors what
 * `supabase/functions/generate-crew-schedule/index.ts` validates: crew,
 * artists (each with their stage), the canonical `briefingStages`, custom
 * subjects, breaks and the existing assignments — normalised to the
 * `{ crewId, artistName, subject, stage, start, end, type }` shape the
 * server's prompt reads.
 */
export function buildGenerateScheduleBody(
  params: BuildScheduleRequestParams,
): Record<string, unknown> {
  const {
    briefingId,
    date,
    crew,
    artists,
    briefingStages,
    customSubjects,
    crewBreaks,
    existingAssignments,
    defaultDuration,
    travelTime,
    slotMinutes = 15,
    regenerateInstructions,
  } = params;

  return {
    briefingId,
    date,
    slotMinutes,
    defaultDuration,
    travelTime,
    crew: crew.map((c) => ({
      id: c.id,
      name: c.name,
      function: c.function ?? "",
      preferredStages: c.preferredStages ?? [],
      noStage: c.noStage ?? false,
    })),
    artists: artists.map((a) => ({
      name: a.name,
      stage: a.stage,
      start: a.start,
      end: a.end,
    })),
    briefingStages: briefingStages.map((s) => ({ id: s.id, label: s.label })),
    customSubjects,
    crewBreaks,
    existingAssignments: existingAssignments.map((a) => ({
      crewId: a.crew_member_id,
      artistName: a.artist_name,
      subject: a.subject,
      stage: a.stage_name,
      start: a.filming_start,
      end: a.filming_end,
      type: a.assignment_type,
    })),
    // Only send when non-empty so the server treats a first-generation
    // request identically whether the field is absent or blank.
    ...(regenerateInstructions ? { regenerateInstructions } : {}),
  };
}
